import type { FunctionalTest } from "./types";

export const FUNCTIONAL_TESTS: FunctionalTest[] = [
  {
    id: "toe-splay",
    name: "Toe Splay Test",
    category: "foot",
    targetSkill: "Toe Splay",
    whatItTells:
      "Whether your toes can spread and move independently — the foundation of a stable, adaptable foot.",
    setup: "Sit or stand barefoot. Try to spread all five toes apart, then lift your big toe while keeping the others down.",
    levels: {
      1: "Cannot spread the toes apart at all — they stay locked together.",
      2: "Toes spread slightly apart with visible effort or clawing.",
      3: "Clear, even splay of all five toes; the big toe can move somewhat independently.",
      4: "Full splay plus the ability to lift and lower the big toe and little toes independently, on both feet.",
    },
  },
  {
    id: "single-leg-balance",
    name: "Single-Leg Balance Test",
    category: "foot",
    targetSkill: "Single-leg Balance",
    whatItTells: "How well your foot, ankle, and hip stabilizers work together under real load.",
    setup: "Stand on one leg, hands on hips, and time how long you can hold a steady position, first eyes open then eyes closed.",
    levels: {
      1: "Less than 10 seconds eyes open before touching down or losing form.",
      2: "30 seconds eyes open with reasonable control.",
      3: "60 seconds eyes open, plus 10 seconds eyes closed.",
      4: "30 seconds eyes closed with a steady, level pelvis throughout.",
    },
  },
  {
    id: "ankle-dorsiflexion",
    name: "Knee-to-Wall Ankle Test",
    category: "foot",
    targetSkill: "Ankle Mobility",
    whatItTells: "How much forward ankle range you have — critical for squatting, walking, and running mechanics.",
    setup: "Kneel facing a wall, toes a set distance away, and drive your knee toward the wall without lifting your heel.",
    levels: {
      1: "Heel lifts with the toes less than 5cm from the wall.",
      2: "Knee touches the wall with toes 5–9cm away, heel down.",
      3: "Knee touches the wall with toes 10–12cm away, heel down, no arch collapse.",
      4: "13cm or more on both sides, with less than 2cm of side-to-side asymmetry.",
    },
  },
  {
    id: "short-foot",
    name: "Short Foot / Arch Control Test",
    category: "foot",
    targetSkill: "Pronation",
    whatItTells: "Whether you can actively control your arch rather than relying on passive structures or footwear.",
    setup: "Standing, try to shorten your foot by drawing the ball of the foot toward the heel without curling your toes.",
    levels: {
      1: "Cannot lift the arch at all without curling the toes.",
      2: "Brief, small arch lift achievable while standing on two feet.",
      3: "Can hold the short-foot position for 30 seconds in single-leg stance.",
      4: "Maintains the short-foot arch through a slow single-leg squat to 45 degrees.",
    },
  },
  {
    id: "calf-raise",
    name: "Single-Leg Calf Raise Test",
    category: "foot",
    targetSkill: "Supination",
    whatItTells: "Your push-off strength and capacity — a major factor in walking, running, and jumping power.",
    setup: "Balance on one leg near a wall for light support and perform full-range calf raises to fatigue.",
    levels: {
      1: "Fewer than 5 full-height reps before form breaks down.",
      2: "10 reps at close to full height.",
      3: "20 reps at full height with no knee bend.",
      4: "25 or more reps with a controlled 3-second lowering phase, equal on both sides.",
    },
  },
  {
    id: "glute-bridge",
    name: "Glute Bridge March Test",
    category: "glute_core",
    targetSkill: "Glute Activation",
    whatItTells: "Whether your glutes — not your hamstrings or lower back — are doing the work of hip extension.",
    setup: "Lie on your back, knees bent, feet flat. Lift into a bridge and hold, then march the knees one at a time.",
    levels: {
      1: "Cannot hold a bridge 30 seconds without hamstring cramping or the pelvis dropping.",
      2: "30-second hold with a level pelvis.",
      3: "10 slow marches per side with the pelvis staying level.",
      4: "20 marches with a 3-second pause at the top of each, zero pelvis rotation.",
    },
  },
  {
    id: "single-leg-bridge",
    name: "Single-Leg Bridge Hold Test",
    category: "glute_core",
    targetSkill: "Glute Strengthening",
    whatItTells: "Single-leg glute strength and pelvis control — closer to how your glutes work in walking and running.",
    setup: "From a bridge position, extend one leg straight and hold the bridge on the working side.",
    levels: {
      1: "Less than 10 seconds before the pelvis drops.",
      2: "20-second hold.",
      3: "40-second hold with a level pelvis.",
      4: "60 seconds on both sides with no hamstring takeover or pelvis rotation.",
    },
  },
  {
    id: "side-plank",
    name: "Side Plank Test",
    category: "glute_core",
    targetSkill: "Core",
    whatItTells: "Lateral core and hip stability — key for single-leg stance, walking, and injury resilience.",
    setup: "Prop up on one forearm, stack your feet, and lift your hips into a straight line from ankle to shoulder.",
    levels: {
      1: "Less than 15 seconds, and only achievable from the knees.",
      2: "30 seconds in a full side plank from the feet.",
      3: "60 seconds with good alignment.",
      4: "60 seconds plus 10 top-leg raises without the hips dropping.",
    },
  },
  {
    id: "deep-squat",
    name: "Deep Squat Hold Test",
    category: "glute_core",
    targetSkill: "Squatting",
    whatItTells: "Combined hip, ankle, and core mobility needed to squat with full depth and control.",
    setup: "Squat down as low as comfortable, heels flat, and hold the bottom position.",
    levels: {
      1: "Cannot reach below parallel without the heels lifting off the ground.",
      2: "20-second bodyweight hold with heels down.",
      3: "60-second relaxed hold with a neutral spine.",
      4: "2-minute hold, able to shift weight side to side while keeping both feet flat.",
    },
  },
  {
    id: "hip-hinge",
    name: "Single-Leg Hip Hinge Test",
    category: "glute_core",
    targetSkill: "Hip Mobility",
    whatItTells: "Balance combined with hip hinge control — a pattern used constantly in daily movement and sport.",
    setup: "Standing on one leg, hinge at the hip and reach the opposite hand toward the floor, keeping the hips square.",
    levels: {
      1: "Loses balance almost immediately.",
      2: "5 reps touching around knee height.",
      3: "8 reps reaching mid-shin with square hips.",
      4: "10 reps touching the floor with square hips, on both sides.",
    },
  },
];
